import { useState, type CSSProperties } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PrivacyRequestStatus, PrivacyRequestType, UserRole } from '@bhops/shared';
import { privacyRequestsApi, type ListPrivacyRequestsQuery } from '@/api/privacy-requests';
import { extractApiError } from '@/api/client';
import { kstDateTime } from '@/lib/format-date';
import { Button, Field, Textarea } from '@/components/Form';
import { ConfirmActionModal } from '@/components/ConfirmActionModal';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';

const STATUS_LABEL: Record<PrivacyRequestStatus, string> = {
  RECEIVED: '접수됨',
  IN_REVIEW: '검토 중',
  FULFILLED: '처리 완료',
  REJECTED: '반려',
};

const TYPE_LABEL: Record<PrivacyRequestType, string> = {
  ACCESS: '열람',
  CORRECTION: '정정',
  DELETION: '삭제',
  RESTRICTION: '처리 정지',
  OTHER: '기타',
};

const LOCALE_LABEL: Record<string, string> = {
  ko: '한국어',
  en: 'English',
  zh: '简体中文',
};

const PAGE_SIZE = 50;

export function PrivacyRequestsInboxPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // This page is ADMIN-gated at the route level, but defense-in-depth: the
  // triage panel only renders its buttons when the current user is admin.
  const canTriage = user?.role === UserRole.ADMIN;

  const [filters, setFilters] = useState<ListPrivacyRequestsQuery>({
    page: 1,
    pageSize: PAGE_SIZE,
    openOnly: true,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [fulfillOpen, setFulfillOpen] = useState(false);
  const [fulfillNote, setFulfillNote] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const listQ = useQuery({
    queryKey: ['privacy-requests', filters],
    queryFn: () => privacyRequestsApi.list(filters),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const selectedQ = useQuery({
    queryKey: ['privacy-request', selectedId],
    queryFn: () => privacyRequestsApi.get(selectedId!),
    enabled: Boolean(selectedId),
  });

  const update = useMutation({
    mutationFn: (args: {
      id: string;
      body: Parameters<typeof privacyRequestsApi.update>[1];
      doneMessage: string;
    }) => privacyRequestsApi.update(args.id, args.body),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ['privacy-requests'] });
      qc.invalidateQueries({ queryKey: ['privacy-request', args.id] });
      toast.success(args.doneMessage);
      setFulfillOpen(false);
      setFulfillNote('');
      setRejectOpen(false);
      setRejectReason('');
    },
    onError: (err) => toast.error(extractApiError(err), '업데이트 실패'),
  });

  const items = listQ.data?.items ?? [];

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            개인정보 요청 (DSAR) 인박스
          </h1>
          <select
            value={filters.status ?? ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                status: (e.target.value || undefined) as PrivacyRequestStatus | undefined,
                page: 1,
              }))
            }
            style={select}
          >
            <option value="">전체 상태</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <select
            value={filters.type ?? ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                type: (e.target.value || undefined) as PrivacyRequestType | undefined,
                page: 1,
              }))
            }
            style={select}
          >
            <option value="">전체 유형</option>
            {Object.entries(TYPE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={Boolean(filters.openOnly)}
              onChange={(e) => setFilters((f) => ({ ...f, openOnly: e.target.checked, page: 1 }))}
            />
            미완료만
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={Boolean(filters.assignedToMe)}
              onChange={(e) =>
                setFilters((f) => ({ ...f, assignedToMe: e.target.checked, page: 1 }))
              }
            />
            나에게 배정
          </label>
          <div
            className="kuun-caption"
            style={{
              marginLeft: 'auto',
              color: listQ.isFetching ? 'var(--brand)' : 'var(--fg-muted)',
            }}
          >
            {listQ.isFetching ? '불러오는 중…' : `${listQ.data?.total ?? 0} 건`}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0 }}>
          {/* List (left) */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-card)',
              background: 'var(--bg-surface)',
              overflow: 'auto',
            }}
          >
            {listQ.isLoading ? (
              <div style={empty}>
                <span className="kuun-caption">불러오는 중…</span>
              </div>
            ) : listQ.error ? (
              <div style={empty}>
                <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(listQ.error)}</span>
              </div>
            ) : items.length === 0 ? (
              <div style={empty} className="kuun-dot-grid">
                <div className="kuun-h4">처리할 DSAR 요청이 없습니다</div>
                <div className="kuun-caption" style={{ marginTop: 4 }}>
                  고객이 개인정보 요청 페이지에서 신청하면 여기에 표시됩니다.
                </div>
              </div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={th}>SLA</th>
                    <th style={th}>접수 번호</th>
                    <th style={th}>상태</th>
                    <th style={th}>유형</th>
                    <th style={th}>언어</th>
                    <th style={th}>신청인</th>
                    <th style={th}>미리보기</th>
                    <th style={th}>제출 시각</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedId(r.id)}
                      style={{
                        cursor: 'pointer',
                        borderTop: '1px solid var(--border-subtle)',
                        background: selectedId === r.id ? 'var(--bg-selected)' : undefined,
                      }}
                    >
                      <td style={td}>
                        {r.slaBreachedAt ? (
                          <span
                            title={`${kstDateTime(r.slaBreachedAt, { label: true }) ?? '–'}에 SLA 30일을 넘겼습니다.`}
                            style={slaBadge}
                          >
                            30일 초과
                          </span>
                        ) : (
                          <span className="kuun-caption">—</span>
                        )}
                      </td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        {r.displayId ?? '–'}
                      </td>
                      <td style={td}>{STATUS_LABEL[r.status]}</td>
                      <td style={td}>{TYPE_LABEL[r.type]}</td>
                      <td style={td}>{LOCALE_LABEL[r.locale] ?? r.locale}</td>
                      <td style={td}>{r.subjectName}</td>
                      <td
                        style={{
                          ...td,
                          maxWidth: 240,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.description}
                      </td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        {/* 추가수정 2차(26-07-27) §2 — 브라우저 로캘 대신 KST 고정.
                            접수 시각은 SLA(30일) 기산점이라 시각까지 남긴다. */}
                        {kstDateTime(r.submittedAt) ?? '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Detail (right) */}
          <div
            style={{
              width: 400,
              flexShrink: 0,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-card)',
              background: 'var(--bg-surface)',
              overflow: 'auto',
            }}
          >
            {!selectedId ? (
              <div style={empty}>
                <span className="kuun-caption">요청을 선택해 주세요.</span>
              </div>
            ) : selectedQ.isLoading ? (
              <div style={empty}>
                <span className="kuun-caption">불러오는 중…</span>
              </div>
            ) : selectedQ.error ? (
              <div style={empty}>
                <span style={{ color: 'var(--fg-danger)' }}>
                  {extractApiError(selectedQ.error)}
                </span>
              </div>
            ) : (
              selectedQ.data && (
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <div className="kuun-label">현재 상태</div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {STATUS_LABEL[selectedQ.data.status]}
                    </div>
                    {selectedQ.data.slaBreachedAt && (
                      <span style={{ ...slaBadge, marginLeft: 'auto' }}>SLA 초과</span>
                    )}
                  </div>
                  <KV k="접수 번호" v={selectedQ.data.displayId ?? '–'} mono />
                  <KV k="유형" v={TYPE_LABEL[selectedQ.data.type]} />
                  <KV k="언어" v={LOCALE_LABEL[selectedQ.data.locale] ?? selectedQ.data.locale} />
                  <KV k="신청인" v={selectedQ.data.subjectName} />
                  <KV k="연락처" v={selectedQ.data.subjectContact} />
                  {selectedQ.data.relatedReportId && (
                    <KV k="관련 리포트" v={selectedQ.data.relatedReportId} mono />
                  )}
                  {selectedQ.data.assignedTo && (
                    <KV k="담당" v={selectedQ.data.assignedTo.displayName} />
                  )}
                  {selectedQ.data.fulfilledAt && (
                    <KV
                      k="완료 시각"
                      v={kstDateTime(selectedQ.data.fulfilledAt, { label: true }) ?? '–'}
                    />
                  )}

                  <div>
                    <div className="kuun-label" style={{ marginBottom: 4 }}>
                      요청 내용
                    </div>
                    <div style={descBox}>{selectedQ.data.description}</div>
                  </div>

                  {selectedQ.data.reviewNote && (
                    <div>
                      <div className="kuun-label" style={{ marginBottom: 4 }}>
                        검토 메모
                      </div>
                      <div style={descBox}>{selectedQ.data.reviewNote}</div>
                    </div>
                  )}

                  {selectedQ.data.rejectedReason && (
                    <div>
                      <div className="kuun-label" style={{ marginBottom: 4 }}>
                        반려 사유
                      </div>
                      <div style={{ ...descBox, color: 'var(--fg-danger)' }}>
                        {selectedQ.data.rejectedReason}
                      </div>
                    </div>
                  )}

                  {canTriage && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        paddingTop: 8,
                        borderTop: '1px solid var(--border-subtle)',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          type="button"
                          onClick={() => {
                            const id = user?.id;
                            if (!id) return;
                            update.mutate({
                              id: selectedQ.data!.id,
                              body: {
                                status: PrivacyRequestStatus.IN_REVIEW,
                                assignedToId: id,
                              },
                              doneMessage: '요청 검토를 시작했습니다.',
                            });
                          }}
                          disabled={update.isPending}
                        >
                          내가 맡아 검토 시작
                        </Button>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          type="button"
                          onClick={() => {
                            setFulfillNote(selectedQ.data?.reviewNote ?? '');
                            setFulfillOpen(true);
                          }}
                          disabled={update.isPending}
                        >
                          처리 완료
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          onClick={() => setRejectOpen(true)}
                          disabled={update.isPending}
                        >
                          반려
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <ConfirmActionModal
        open={fulfillOpen}
        onClose={() => {
          setFulfillOpen(false);
          setFulfillNote('');
        }}
        title="처리 완료 확인"
        context={
          selectedQ.data
            ? `${selectedQ.data.displayId ?? '–'} · ${TYPE_LABEL[selectedQ.data.type]} · ${selectedQ.data.subjectName}`
            : undefined
        }
        submitLabel="처리 완료"
        isSubmitting={update.isPending}
        onSubmit={() => {
          if (!selectedQ.data) return;
          update.mutate({
            id: selectedQ.data.id,
            body: { status: PrivacyRequestStatus.FULFILLED, reviewNote: fulfillNote || undefined },
            doneMessage: '요청 처리를 마쳤습니다.',
          });
        }}
      >
        <Field label="검토 메모 (선택)">
          <Textarea
            value={fulfillNote}
            onChange={(e) => setFulfillNote(e.target.value)}
            placeholder="처리 내용을 적어 주세요"
            rows={4}
          />
        </Field>
      </ConfirmActionModal>

      <ConfirmActionModal
        open={rejectOpen}
        onClose={() => {
          setRejectOpen(false);
          setRejectReason('');
        }}
        title="반려 처리"
        context={
          selectedQ.data
            ? `${selectedQ.data.displayId ?? '–'} · ${TYPE_LABEL[selectedQ.data.type]} · ${selectedQ.data.subjectName}`
            : undefined
        }
        submitLabel="반려"
        submitVariant="danger"
        submitDisabled={!rejectReason.trim()}
        isSubmitting={update.isPending}
        onSubmit={() => {
          if (!selectedQ.data || !rejectReason.trim()) return;
          update.mutate({
            id: selectedQ.data.id,
            body: { status: PrivacyRequestStatus.REJECTED, rejectedReason: rejectReason.trim() },
            doneMessage: '요청을 반려했습니다.',
          });
        }}
      >
        <Field label="반려 사유" required>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="반려 사유를 적어 주세요"
            rows={4}
            required
          />
        </Field>
      </ConfirmActionModal>
    </>
  );
}

function KV({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
      <div style={{ width: 84, color: 'var(--fg-muted)' }}>{k}</div>
      <div style={mono ? { fontFamily: 'var(--font-mono)' } : undefined}>{v}</div>
    </div>
  );
}

const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const empty: CSSProperties = {
  height: '100%',
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--fg-muted)',
  padding: 32,
};
const select: CSSProperties = {
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontSize: 13,
};
const slaBadge: CSSProperties = {
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: 'var(--r-pill)',
  fontSize: 10,
  fontWeight: 700,
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  border: '1px solid var(--fg-danger)',
};
const descBox: CSSProperties = {
  padding: 10,
  background: 'var(--bg-sunken)',
  borderRadius: 'var(--r-card)',
  fontSize: 13,
  whiteSpace: 'pre-wrap',
};
